import {
  canHelpIn,
  claimsLead,
  isPendingConfirmation,
  leadsIn,
  LEVELS,
  type Level,
  RANKS,
  type Rank,
  REF_SCORE,
} from '@dc1/shared';
import type { Dashboard, Progress } from '../../api/queries';
import type { ContentView } from '../../lib/content';

// Pure derivations for the team dashboard. Scores and standings come from the API (computed with
// @dc1/shared); this file only arranges them for the charts.

export type DashMember = Dashboard['members'][number];
export type Zone = 'below' | Rank;

/** The highest Level whose reference score the score has reached, or 'below' under Junior's. */
export function zoneOf(score: number): Zone {
  let zone: Zone = 'below';
  for (const r of RANKS) if (score >= REF_SCORE[r]) zone = r;
  return zone;
}

/** Background bands of the ranking chart: below Junior, then one band per Level up to 100. */
export function zoneBands(): { zone: Zone; from: number; to: number }[] {
  const first = RANKS[0];
  return [
    { zone: 'below', from: 0, to: REF_SCORE[first] },
    ...RANKS.map((r, i) => {
      const next = RANKS[i + 1];
      return { zone: r, from: REF_SCORE[r], to: next ? REF_SCORE[next] : 100 };
    }),
  ];
}

/** A 0–100 group score drawn with the L1–L5 colour it corresponds to (0 → L1, 100 → L5). */
export function levelForScore(score: number): Level {
  const top = LEVELS.length - 1;
  const step = Math.round(1 + (Math.max(0, Math.min(100, score)) / 100) * top);
  return LEVELS[Math.max(0, Math.min(top, step - 1))] ?? 1;
}

export type AreaCell =
  { kind: 'na' } | { kind: 'none' } | { kind: 'level'; self: Level; effective: Level; pending: boolean };

/** One person × area cell: N/A, not assessed, or the self level (hatched over what counts while unconfirmed). */
export function areaCell(level: DashMember['levels'][number] | undefined): AreaCell {
  if (!level) return { kind: 'none' };
  if (level.isNa) return { kind: 'na' };
  if (level.selfLevel === null) return { kind: 'none' };
  const self = level.selfLevel as Level;
  const effective = (level.effectiveLevel ?? self) as Level;
  return { kind: 'level', self, effective, pending: effective < self };
}

export interface CoverageBar {
  areaId: string;
  name: string;
  count: number;
  risk: boolean;
}

/** Coverage grouped like the content, in content order. */
export function coverageByGroup(
  coverage: Dashboard['coverage'],
  content: ContentView,
): { groupId: string; n: number; short: string; bars: CoverageBar[] }[] {
  const byArea = new Map(coverage.map((c) => [c.areaId, c]));
  return content.groups.map((g) => ({
    groupId: g.id,
    n: g.n,
    short: g.short,
    bars: g.areas.flatMap((a) => {
      const c = byArea.get(a.id);
      return c ? [{ areaId: a.id, name: a.name, count: c.count, risk: c.risk }] : [];
    }),
  }));
}

/**
 * Angles of the coverage rose, starting at 12 o'clock: one slot per area and an empty slot after each group;
 * a petal takes the middle 82% of its slot. `mid` is the angle of the group's label.
 */
export function roseSlots(
  groupSizes: readonly number[],
): { petals: { a0: number; a1: number }[]; mid: number }[] {
  const step = (Math.PI * 2) / (groupSizes.reduce((sum, n) => sum + n, 0) + groupSizes.length);
  let slot = 0.5;
  return groupSizes.map((n) => {
    const first = slot;
    slot += n + 1;
    return {
      petals: Array.from({ length: n }, (_, i) => {
        const a0 = -Math.PI / 2 + (first + i) * step + step * 0.09;
        return { a0, a1: a0 + step * 0.82 };
      }),
      mid: -Math.PI / 2 + (first + n / 2) * step,
    };
  });
}

/** Coverage petal shade 1–4: the share of the team at L4+ in that area, in quarters. */
export function coverageShade(count: number, teamSize: number): 1 | 2 | 3 | 4 {
  const quarter = Math.ceil((count / Math.max(1, teamSize)) * 4);
  return Math.max(1, Math.min(4, quarter)) as 1 | 2 | 3 | 4;
}

/**
 * Head count where risk ends, to draw on the rose: halfway between the largest risk count and the smallest
 * safe one. The API decides what a risk is; with no risk area or no safe one there is no edge to draw.
 */
export function riskEdge(coverage: readonly { count: number; risk: boolean }[]): number | null {
  const risky = coverage.filter((c) => c.risk).map((c) => c.count);
  const safe = coverage.filter((c) => !c.risk).map((c) => c.count);
  if (!risky.length || !safe.length) return null;
  return (Math.max(...risky) + Math.min(...safe)) / 2;
}

/** "4. Tiến bộ trong kỳ" lists this many people (biggest gain first) until "Show all", as design v3 does. */
export const PERIOD_PROGRESS_TOP = 12;

export interface PeriodProgressRow {
  member: DashMember;
  prev: number | null;
  now: number;
  delta: number | null;
}

/** The period the dashboard is about, and the closed period right before it. */
function currentAndPrevious(progress: Progress, currentPeriodId: string) {
  const periods = [...progress.periods].sort((a, b) => a.index - b.index);
  const current = periods.find((p) => p.id === currentPeriodId);
  const previous = periods
    .filter((p) => p.status === 'closed' && (!current || p.index < current.index))
    .at(-1);
  return { periods, previous };
}

/** Confirmed score now vs. the previous period's snapshot, biggest gain first; people without history last. */
export function periodProgress(
  members: readonly DashMember[],
  progress: Progress,
  currentPeriodId: string,
): PeriodProgressRow[] {
  const { previous } = currentAndPrevious(progress, currentPeriodId);
  const history = new Map(progress.members.map((m) => [m.id, m.points]));
  const rows = members.map((member) => {
    const point = previous ? history.get(member.id)?.find((p) => p.periodId === previous.id) : undefined;
    const prev = point ? point.effectiveScore : null;
    return {
      member,
      prev,
      now: member.effectiveScore,
      delta: prev === null ? null : member.effectiveScore - prev,
    };
  });
  return rows.sort((a, b) => (b.delta ?? Number.NEGATIVE_INFINITY) - (a.delta ?? Number.NEGATIVE_INFINITY));
}

export interface TrendRow {
  id: string;
  name: string;
  /** Confirmed score per period column; null where the person has no snapshot. */
  values: (number | null)[];
  now: number | null;
  /** Latest value minus the one before it. */
  delta: number | null;
}

/** Columns are every period in order (the open one last); rows follow the given member order. */
export function teamTrend(
  progress: Progress,
  order: readonly { id: string; displayName: string }[],
): { periods: Progress['periods']; rows: TrendRow[]; hasClosed: boolean } {
  const periods = [...progress.periods].sort((a, b) => a.index - b.index);
  const byId = new Map(progress.members.map((m) => [m.id, m]));
  const rows = order.flatMap((o) => {
    const m = byId.get(o.id);
    if (!m) return [];
    const values = periods.map((p) => m.points.find((pt) => pt.periodId === p.id)?.effectiveScore ?? null);
    const known = values.filter((v): v is number => v !== null);
    const now = known.at(-1) ?? null;
    const before = known.at(-2);
    return [
      {
        id: m.id,
        name: o.displayName,
        values,
        now,
        delta: now !== null && before !== undefined ? now - before : null,
      },
    ];
  });
  return { periods, rows, hasClosed: periods.some((p) => p.status === 'closed') };
}

/** "+5", "0", "-3"; "–" when there is nothing to compare with. */
/**
 * Shared scale of the progress dumbbells: every previous and current score, padded by 4 and rounded out to
 * multiples of 5, within 0–100. Taken over all rows, so it does not move when the list expands.
 */
export function progressScale(rows: readonly PeriodProgressRow[]): { lo: number; hi: number } {
  const values = rows.flatMap((r) => (r.prev === null ? [r.now] : [r.prev, r.now]));
  if (!values.length) return { lo: 0, hi: 100 };
  const lo = Math.max(0, Math.floor((Math.min(...values) - 4) / 5) * 5);
  const hi = Math.min(100, Math.ceil((Math.max(...values) + 4) / 5) * 5);
  return { lo, hi: Math.max(hi, lo + 1) };
}

/** Position of a score on that scale, 0–100 (%). */
export function scaleAt(score: number, { lo, hi }: { lo: number; hi: number }): number {
  return ((score - lo) / (hi - lo)) * 100;
}

export function formatDelta(delta: number | null): string {
  if (delta === null) return '–';
  return delta > 0 ? `+${delta}` : String(delta);
}

export function deltaTone(delta: number | null): 'up' | 'down' | 'flat' {
  if (delta === null || delta === 0) return 'flat';
  return delta > 0 ? 'up' : 'down';
}

/** Dots closer than this (score points) on the same Level lane are moved apart vertically. */
const SPREAD_GAP = 2.6;
/** Vertical offsets (px) tried in turn for a dot that would overlap another one. */
const SPREAD_OFFSETS = [0, -16, 16, -32, 32, -48, 48] as const;

export interface SpreadDot {
  member: DashMember;
  /** Vertical offset in px, so close scores stay readable. */
  dy: number;
}

/**
 * "Phân bố điểm năng lực" (design v3): one lane per Level, highest first, each person a dot at their
 * confirmed score; dots that would overlap are moved up or down.
 */
export function spreadLanes(members: readonly DashMember[]): { rank: Rank; dots: SpreadDot[] }[] {
  return [...RANKS].reverse().map((rank) => {
    const placed: { slot: number; x: number }[] = [];
    const dots = members
      .filter((m) => m.rank === rank)
      .sort((a, b) => a.effectiveScore - b.effectiveScore)
      .map((member) => {
        const x = member.effectiveScore;
        let slot = 0;
        while (
          slot < SPREAD_OFFSETS.length - 1 &&
          placed.some((p) => p.slot === slot && Math.abs(p.x - x) < SPREAD_GAP)
        )
          slot += 1;
        placed.push({ slot, x });
        return { member, dy: SPREAD_OFFSETS[slot] ?? 0 };
      });
    return { rank, dots };
  });
}

/** How the period went for the team: how many rose, fell or held, and the average change per person. */
export function progressSummary(rows: readonly PeriodProgressRow[]): {
  up: number;
  down: number;
  flat: number;
  average: number | null;
} {
  const deltas = rows.flatMap((r) => (r.delta === null ? [] : [r.delta]));
  return {
    up: deltas.filter((d) => d > 0).length,
    down: deltas.filter((d) => d < 0).length,
    flat: deltas.filter((d) => d === 0).length,
    average: deltas.length ? Math.round((deltas.reduce((a, b) => a + b, 0) / deltas.length) * 10) / 10 : null,
  };
}

/** Areas where a person's self level waits for a confirmation, in content order. */
export function pendingAreas(member: DashMember): { areaId: string; level: Level }[] {
  return member.levels.flatMap((l) =>
    !l.isNa &&
    l.selfLevel !== null &&
    isPendingConfirmation(l.selfLevel as Level, l.confirmedLevel as Level | null)
      ? [{ areaId: l.areaId, level: l.selfLevel as Level }]
      : [],
  );
}

/** For each risk area, the people who do cover it (confirmed L4+); empty when nobody does. */
export function riskCoverers(
  members: readonly DashMember[],
  coverage: Dashboard['coverage'],
): { areaId: string; count: number; names: string[] }[] {
  return coverage
    .filter((c) => c.risk)
    .map((c) => ({
      areaId: c.areaId,
      count: c.count,
      names: members
        .filter((m) => {
          const l = m.levels.find((x) => x.areaId === c.areaId);
          return (
            l !== undefined && canHelpIn({ isNa: l.isNa, confirmedLevel: l.confirmedLevel as Level | null })
          );
        })
        .map((m) => m.displayName),
    }));
}

/** A member's level in one area, as the shared rules read it. */
const asArea = (l: DashMember['levels'][number]) => ({
  isNa: l.isNa,
  selfLevel: l.selfLevel as Level | null,
  confirmedLevel: l.confirmedLevel as Level | null,
});

function areaOf(member: DashMember, areaId: string) {
  const l = member.levels.find((x) => x.areaId === areaId);
  return l ? asArea(l) : undefined;
}

/** One area: who leads it at a confirmed L5, and who claims L5 there, waiting for a confirmation (ADR 0018). */
export interface AreaLeads {
  areaId: string;
  leads: DashMember[];
  claims: DashMember[];
}

export function areaLeads(members: readonly DashMember[], areaIds: readonly string[]): AreaLeads[] {
  return areaIds.map((areaId) => {
    const where = (rule: (a: NonNullable<ReturnType<typeof areaOf>>) => boolean) =>
      members.filter((m) => {
        const a = areaOf(m, areaId);
        return a !== undefined && rule(a);
      });
    return { areaId, leads: where(leadsIn), claims: where(claimsLead) };
  });
}

/** One person's L5 areas: confirmed, and claimed while waiting. */
export interface PersonLeads {
  member: DashMember;
  leads: string[];
  claims: string[];
}

/** People with an L5, confirmed or claimed; most confirmed first, then most claimed, then by name. */
export function personLeads(members: readonly DashMember[]): PersonLeads[] {
  return members
    .map((member) => {
      const areas = member.levels.map((l) => ({ id: l.areaId, a: asArea(l) }));
      return {
        member,
        leads: areas.filter((x) => leadsIn(x.a)).map((x) => x.id),
        claims: areas.filter((x) => claimsLead(x.a)).map((x) => x.id),
      };
    })
    .filter((p) => p.leads.length + p.claims.length > 0)
    .sort(
      (a, b) =>
        b.leads.length - a.leads.length ||
        b.claims.length - a.claims.length ||
        a.member.displayName.localeCompare(b.member.displayName),
    );
}
